import { useState, useEffect, useRef } from 'react';
import { SavChatRepository } from '@/services/firebase/sav-chat-repository';
import { SavChat } from '@/models/savChat';
import { useAuth } from '@/app/providers';
import { isSavChatEnabled } from '@/shared/config/featureFlags';

/**
 * Hook pour gérer les notifications de nouveaux messages SAV côté admin
 * Version globale : écoute toutes les organisations dont l'utilisateur est admin
 */
export const useAdminSavNotifications = () => {
    const { currentUser, affiliations } = useAuth();

    // Stocker l'état des chats précédents pour chaque orga: Map<OrgaId, Map<ChatId, UnreadCount>>
    const previousChatsStateRef = useRef<Map<string, Map<string, number>>>(new Map());
    // Pour éviter les notifications au chargement initial
    const isFirstLoadRef = useRef(true);

    // State pour stocker les chats non lus par organisation
    const [chatsByOrg, setChatsByOrg] = useState<Record<string, SavChat[]>>({});

    // State pour la dernière notification (pour l'animation de la cloche)
    const [lastNotification, setLastNotification] = useState<{ chat: SavChat, timestamp: number } | null>(null);

    // Gérer le flag de premier chargement
    useEffect(() => {
        const timer = setTimeout(() => {
            isFirstLoadRef.current = false;
        }, 2000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!currentUser || !affiliations || affiliations.length === 0) {
            return;
        }

        // Filtrer les organisations pertinentes (admin/owner + SAV activé)
        const relevantAffiliations = affiliations.filter(aff =>
            (aff.role === 'admin' || aff.role === 'owner') &&
            aff.status === 'active' &&
            isSavChatEnabled(aff.organizationId)
        );

        if (relevantAffiliations.length === 0) return;

        const unsubscribes: (() => void)[] = [];
        
        // Tracker les premiers snapshots pour chaque organisation
        const firstSnapshotReceivedForOrg = new Map<string, boolean>();

        relevantAffiliations.forEach(aff => {
            const orgId = aff.organizationId;

            // Initialiser la map pour cette orga si besoin
            if (!previousChatsStateRef.current.has(orgId)) {
                previousChatsStateRef.current.set(orgId, new Map());
            }
            
            // Marquer qu'on n'a pas encore reçu de snapshot pour cette orga
            firstSnapshotReceivedForOrg.set(orgId, false);

            const unsubscribe = SavChatRepository.subscribeToOrganizationChats(orgId, (chats) => {
                const previousOrgaChats = previousChatsStateRef.current.get(orgId) || new Map();
                const currentOrgaChats = new Map<string, number>();
                
                // Vérifier si c'est le premier snapshot pour cette organisation
                const isFirstSnapshotForOrg = !firstSnapshotReceivedForOrg.get(orgId);

                let hasNewMessage = false;
                let lastNewMessageChat: SavChat | null = null;
                const currentUnreadChats: SavChat[] = [];

                chats.forEach(chat => {
                    const unread = chat.unreadByAdmin || 0;
                    currentOrgaChats.set(chat.id, unread);

                    if (unread > 0) {
                        currentUnreadChats.push(chat);
                    }

                    const prevUnread = previousOrgaChats.get(chat.id) || 0;

                    // On ne notifie que si :
                    // 1. Ce n'est pas le premier chargement global de l'application
                    // 2. Ce n'est pas le premier snapshot reçu pour cette organisation
                    // 3. Le nombre de messages non lus a augmenté
                    if (!isFirstLoadRef.current && !isFirstSnapshotForOrg && unread > prevUnread) {
                        hasNewMessage = true;
                        lastNewMessageChat = chat;
                    }
                });

                // Marquer que le premier snapshot a été reçu
                firstSnapshotReceivedForOrg.set(orgId, true);
                
                // Mettre à jour l'état pour la détection de prochains messages
                previousChatsStateRef.current.set(orgId, currentOrgaChats);

                // Mettre à jour la liste des chats non lus pour l'interface
                setChatsByOrg(prev => ({
                    ...prev,
                    [orgId]: currentUnreadChats
                }));

                // Notifier si nouveau message
                if (hasNewMessage && lastNewMessageChat) {
                    setLastNotification({
                        chat: lastNewMessageChat,
                        timestamp: Date.now()
                    });
                }
            });

            unsubscribes.push(unsubscribe);
        });

        return () => {
            unsubscribes.forEach(u => u());
        };
    }, [affiliations, currentUser]);

    // Retourner la liste aplatie des chats non lus, triée par date
    const allUnreadChats = Object.values(chatsByOrg).flat().sort((a, b) => {
        const dateA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const dateB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return dateB - dateA;
    });

    return { unreadChats: allUnreadChats, lastNotification };
};
